import Text from "@ui-kit/typography/Text/Text.web";
import ResponsiveDialog from "@ui-kit/overlays/ResponsiveDialog/ResponsiveDialog.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo, useEffect, useRef } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import CartTimer from "@shared/components/application/data-display/CartTimer/CartTimer.web";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { ExpressCheckoutSection } from "@app-contexts/public/features/detail-pages/components/modals/ExpressCheckoutSection.web";
import { EnhancedCartSummaryAnalyticsProvider } from "./EnhancedCartSummaryAnalyticsContext";
import EnhancedCartSummaryContent from "./EnhancedCartSummaryContent.web";
import type { EnhancedCartSummaryProps } from "./EnhancedCartSummary.props";

export default function EnhancedCartSummary({
  isOpen,
  onClose,
  displayMode,
  onCheckout,
  dynamicHoldParams,
  analyticsSource,
  venue,
  sourceStep = "enhanced_cart_summary",
  venueId,
}: EnhancedCartSummaryProps): JSX.Element {
  const { t } = useTranslation("purchases");
  const { totalItemQuantity, totalPrice, hasItems } = usePublicBasket();
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      prevOpenRef.current = true;
      UserInteractionLogger.buttonClick({
        label: "view_cart",
        custom_params: {
          cart_version: "enhanced",
          item_count: totalItemQuantity ?? 0,
          cart_total: totalPrice ?? 0,
          ...(analyticsSource && { source: analyticsSource }),
        },
      });
    }
    if (!isOpen) prevOpenRef.current = false;
  }, [isOpen, totalItemQuantity, totalPrice, analyticsSource]);

  // Close drawer/modal when all items have been removed
  useEffect(() => {
    if (isOpen && !hasItems) {
      onClose();
    }
  }, [isOpen, hasItems, onClose]);

  const analyticsContextValue = useMemo(
    () => ({
      cartVersion: "enhanced" as const,
      ...(analyticsSource && { source: analyticsSource }),
    }),
    [analyticsSource]
  );

  const content = (
    <EnhancedCartSummaryAnalyticsProvider value={analyticsContextValue}>
      <EnhancedCartSummaryContent
        onCheckout={onCheckout}
        dynamicHoldParams={dynamicHoldParams}
        inset
        renderAfterFooter={
          isOpen ? (
            <ExpressCheckoutSection
              venueId={venue?.id ?? venueId}
              sourceStep={sourceStep}
            />
          ) : undefined
        }
      />
    </EnhancedCartSummaryAnalyticsProvider>
  );

  const cartTitle = (
    <Text as="span" fontWeight="bold">
      {t("Cart")}
    </Text>
  );
  const cartHeader = hasItems ? (
    <>
      {cartTitle}
      <CartTimer.Checkout />
    </>
  ) : (
    cartTitle
  );

  const headerStyleOverrides = {
    display: "grid" as const,
    gridTemplateColumns: hasItems
      ? ("1fr auto 1fr" as const)
      : ("1fr auto" as const),
    alignItems: "center" as const,
    borderBottom: "1px solid",
    borderColor: "border",
    pr: "xs" as const,
    onClose,
    closeButtonProps: { justifySelf: "end" as const },
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      forceDisplayMode={displayMode}
      headerComponent={cartHeader}
      headerContainerProps={headerStyleOverrides}
      bodyComponent={content}
      bodyContainerProps={{
        p: "none",
        pt: "lg",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
      drawerOverlayProps={{
        backdropFilter: "blur(5px)",
      }}
      placement={displayMode === "drawer" ? "right" : undefined}
      size={displayMode === "drawer" ? "md" : "4xl"}
      isCentered={displayMode === "modal"}
      scrollBehavior={displayMode === "modal" ? "inside" : undefined}
    />
  );
}
